import { sortByKey } from '@semios/app-platform-common'
import { getXDateFormat } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/_utils/getXDateFormat'
import { NameWithExternalVendorIcons } from 'components/NameWithExternalVendorIcons/NameWithExternalVendorIcons'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { alphaColorReplacer, colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getCropNameFromId } from 'utils/getCropNameFromId'
import { getExternalVendorNamesByIrrigationZoneId } from 'utils/getExternalVendorNamesByIrrigationZoneId'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { useIrrigationZoneNameDictionary } from '../_utils/useIrrigationZoneNameDictionary'
import { CommentTextarea } from '../CommentTextarea'
import { IcsChart } from '../IcsChart'
import type { TIcsDashboardData } from '../types'

export const YTDEtcVsApplied = ({ data, resetKey }: { data: TIcsDashboardData['ytd']; resetKey: number }) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const irrigationZoneEmitters = selectedProperty?.irrigationZoneEmitters ?? {}
  const unitSuffix = unitConverter.evapotranspiration().suffix()
  const irrigationZoneNameDictionary = useIrrigationZoneNameDictionary(irrigationZoneEmitters)

  if (!selectedPropertyId || !selectedProperty?.timezone) return null

  const { timezone } = selectedProperty
  const firstForecastTimestamp = moment.tz(timezone).valueOf() + 1000

  const externalVendorNamesByZoneId = getExternalVendorNamesByIrrigationZoneId({
    irrigationZoneEmitters: Object.values(irrigationZoneEmitters),
    points: Object.values(selectedProperty.points ?? {}),
  })

  const shapedData = (data ?? [])
    .flatMap(
      ({
        zone_id,
        cumulative_et,
        cumulative_ia,
      }: {
        zone_id: number
        cumulative_et: {
          blockId: number
          evapotranspirationETc: {
            timestamp: string
            value: number | string | null
          }[]
        }[]
        cumulative_ia: {
          timestamp: string
          value: number
        }[]
      }) => {
        const irrigationZoneName = irrigationZoneNameDictionary[zone_id]

        if (!irrigationZoneName) return []

        const blockData = cumulative_et.map(({ blockId, evapotranspirationETc }) => {
          return {
            blockId,
            blockName:
              selectedProperty.blocks?.[blockId]?.name ?? translate.phrases.banyanApp('Unknown Block'),
            cropId: selectedProperty.blocks?.[blockId]?.cropIds?.[0],
            data: evapotranspirationETc,
          }
        })

        return {
          irrigationZoneId: zone_id,
          irrigationZoneName:
            irrigationZoneNameDictionary[zone_id] ?? translate.phrases.banyanApp('Unknown Zone'),
          externalVendorNames: externalVendorNamesByZoneId[zone_id],
          blocks: blockData,
          irrigationActivity: cumulative_ia,
        }
      },
    )
    .sort(sortByKey('irrigationZoneName'))

  return (
    <div>
      <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{translate.phrases.banyanApp('Year to Date ETc vs Applied')}</h3>
        <div css={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              css={{
                width: 24,
                height: 3,
                backgroundColor: colors.lineChartSeriesTwo,
                borderRadius: 2,
              }}
            />
            <span css={{ fontSize: 13 }}>{`${translate.phrases.banyanApp('ETc')} (${unitSuffix})`}</span>
          </div>
          <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              css={{
                width: 24,
                height: 3,
                backgroundColor: colors.applied,
                borderRadius: 2,
              }}
            />
            <span css={{ fontSize: 13 }}>{`${translate.phrases.banyanApp('Applied')} (${unitSuffix})`}</span>
          </div>
        </div>
      </div>
      {shapedData.map((datum) => {
        return (
          <div key={`${datum?.irrigationZoneId}`}>
            <b css={{ display: 'block', textAlign: 'center', textDecoration: 'underline', marginBottom: 10 }}>
              <NameWithExternalVendorIcons
                name={`${datum?.irrigationZoneName} (${getCropNameFromId(datum?.blocks?.[0]?.cropId)})`}
                externalVendorNames={datum?.externalVendorNames}
              />
            </b>
            <IcsChart
              chart={{
                semiosHighchartsAdditions: {
                  id: `ytd-etc-vs-applied-${datum?.irrigationZoneId}`,
                  stackedChartId: 'YTD-Et-IA',
                  firstForecastTimestamp,
                },
                tooltip: {
                  enabled: false,
                  xDateFormat: getXDateFormat(true),
                },
                chart: {
                  type: 'line',
                  height: 300,
                  marginLeft: 150,
                  spacingTop: 20,
                  marginTop: 20,
                  events: {
                    selection: function () {
                      return false
                    },
                  },
                },
                title: {
                  text: undefined,
                },
                yAxis: {
                  title: {
                    text: `${translate.phrases.banyanApp('YTD Water')} (${unitSuffix})`,
                    x: -50,
                  },
                },
                xAxis: {
                  type: 'datetime',
                  crosshair: false,
                  max: moment.tz(timezone).endOf('year').valueOf(),
                  tickPositions: Array.from({ length: 12 }, (_, i) => {
                    const date = moment
                      .tz(timezone)
                      .year(moment.tz(timezone).year())
                      .month(i)
                      .startOf('month')

                    return date.valueOf()
                  }),
                  labels: {
                    format: '{value:%b}',
                  },
                  plotBands: [
                    {
                      id: 'plotBand-forecast',
                      color: alphaColorReplacer(colors.grey200, 0.25),
                      from: firstForecastTimestamp,
                      to: Number.MAX_SAFE_INTEGER,
                      zIndex: 1,
                    },
                  ],
                },
                series: [
                  {
                    name: 'ETc',
                    type: 'line',
                    color: colors.lineChartSeriesTwo,
                    // Just showing one block right now, could show all
                    data: (datum?.blocks[0]?.data ?? []).map(
                      (item: { timestamp: string; value: number | string | null }) => {
                        const unixTimestamp = moment.tz(item.timestamp, timezone).valueOf()
                        const convertedValue = unitConverter.evapotranspiration(Number(item.value)).value()

                        return {
                          x: unixTimestamp,
                          y: convertedValue,
                        }
                      },
                    ),
                  },
                  {
                    name: 'Applied',
                    type: 'line',
                    color: colors.applied,
                    data: (datum?.irrigationActivity ?? []).map((item) => {
                      const unixTimestamp = moment.tz(item.timestamp, timezone).valueOf()
                      const convertedValue = unitConverter.precipitation(Number(item.value)).value()

                      return {
                        x: unixTimestamp,
                        y: convertedValue,
                      }
                    }),
                  },
                ],
              }}
              chartVisible={true}
              timezone={timezone}
            />
          </div>
        )
      })}
      <CommentTextarea resetKey={resetKey} />
    </div>
  )
}
